/**
 * Shared helpers for subscription pricing / user-subscription module tabs.
 */

const TAB_ORDER = ['task', 'document', 'invoice_scanner', 'discussion_forum'];

export const MODULE_TAB_ICONS = {
  task: 'fas fa-tasks',
  document: 'fas fa-file-alt',
  invoice_scanner: 'fas fa-file-invoice',
  discussion_forum: 'fas fa-comments',
};

const MODULE_TAB_LABELS = {
  task: 'Tasks',
  document: 'Documents',
  invoice_scanner: 'Snap Bill',
  discussion_forum: 'Discussion Forum',
};

/** Normalize API module names to stable tab keys used in URLs (?module=). */
export function normalizeModuleTabKey(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).toLowerCase().trim().replace(/\s+/g, '_');
  if (s === 'task' || s === 'tasks' || s.includes('task_management')) return 'task';
  if (s === 'document' || s === 'documents') return 'document';
  if (s.includes('invoice') || s.includes('snap')) return 'invoice_scanner';
  if (s.includes('forum') || s.includes('discussion')) return 'discussion_forum';
  return s;
}

export function getModuleTabLabel(tabKey, fallback) {
  return MODULE_TAB_LABELS[tabKey] || fallback || tabKey;
}

export function getModuleTabIcon(tabKey) {
  return MODULE_TAB_ICONS[tabKey] || 'fas fa-cube';
}

export function planMatchesModuleTab(plan, tabKey) {
  if (!plan || !tabKey) return false;
  const tab = normalizeModuleTabKey(tabKey);
  if (plan.Module) {
    const planKey = normalizeModuleTabKey(plan.Module.name || plan.Module.display_name);
    return planKey === tab;
  }
  if (plan.module_id != null) {
    if (tab === 'task') return Number(plan.module_id) === 1;
    if (tab === 'document') return Number(plan.module_id) === 2;
  }
  return false;
}

export function subscriptionMatchesModuleTab(subscription, tabKey) {
  if (!subscription || !tabKey) return false;
  const tab = normalizeModuleTabKey(tabKey);
  const mod = subscription.SubscriptionPlan?.Module;
  if (mod) {
    return normalizeModuleTabKey(mod.name || mod.display_name) === tab;
  }
  const moduleId = subscription.SubscriptionPlan?.module_id;
  if (moduleId != null) {
    if (tab === 'task') return Number(moduleId) === 1;
    if (tab === 'document') return Number(moduleId) === 2;
  }
  return false;
}

/** Build tab list from plans that include Module metadata. */
export function getModuleTabsFromPlans(plans) {
  const seen = new Map();
  for (const plan of plans || []) {
    const mod = plan.Module;
    if (!mod) continue;
    const key = normalizeModuleTabKey(mod.name || mod.display_name);
    if (!key || seen.has(key)) continue;
    seen.set(key, {
      key,
      label: mod.display_name || getModuleTabLabel(key, mod.name),
      description: mod.description || '',
      module_id: mod.id ?? plan.module_id,
      sort_order: mod.sort_order ?? plan.sort_order ?? 0,
    });
  }
  const tabs = Array.from(seen.values());
  tabs.sort((a, b) => {
    const ai = TAB_ORDER.indexOf(a.key);
    const bi = TAB_ORDER.indexOf(b.key);
    const ao = ai === -1 ? 100 + (a.sort_order || 0) : ai;
    const bo = bi === -1 ? 100 + (b.sort_order || 0) : bi;
    return ao - bo;
  });
  return tabs;
}

export function resolveInitialModuleTab(moduleParam, plans, fallback = 'task') {
  if (moduleParam) {
    const normalized = normalizeModuleTabKey(moduleParam);
    const tabs = getModuleTabsFromPlans(plans);
    if (tabs.some((t) => t.key === normalized)) return normalized;
    if (normalized) return normalized;
  }
  const tabs = getModuleTabsFromPlans(plans);
  return tabs[0]?.key || fallback;
}

export function getModuleDescriptionForTab(activeTab, { modulesList = [], subscriptionPlans = [] } = {}) {
  const tab = normalizeModuleTabKey(activeTab);
  const staticDescriptions = {
    task:
      'Streamline your workflow with our comprehensive task management solution. Organize, assign, and track tasks with ease.',
    document:
      'Securely store, organize, and manage all your business documents in one centralized location.',
    invoice_scanner:
      'Snap Bill — AI-powered invoice and quotation scanning. Extract data from PDFs and images, match vendors, and push to Zoho Books.',
    discussion_forum:
      'Collaborate with your team in business discussion forums with attachments and notifications.',
  };

  if (Array.isArray(modulesList) && modulesList.length > 0) {
    const module = modulesList.find((mod) => {
      const name = normalizeModuleTabKey(mod.ModuleName || mod.name || mod.display_name);
      return name === tab;
    });
    if (module?.Description || module?.description) {
      return module.Description || module.description;
    }
  }

  const planWithModule = (subscriptionPlans || []).find((plan) => planMatchesModuleTab(plan, tab));
  if (planWithModule?.Module?.description) {
    return planWithModule.Module.description;
  }

  return staticDescriptions[tab] || '';
}

/** Fetch all active plans (backend paginates by default). */
export const SUBSCRIPTION_PLANS_FETCH_LIMIT = 100;

export function subscriptionPlansApiUrl() {
  return `/subscription/plans?limit=${SUBSCRIPTION_PLANS_FETCH_LIMIT}`;
}
